import { forwardRef } from 'react'
import { Mic, MicOff, PhoneOff } from 'lucide-react'
import { TalkingAvatar, type TalkingAvatarHandle, type AvatarStatus } from './TalkingAvatar'
import { cn } from '@/lib/utils'
import type { Companion } from '../data/companions'
import type { Mood } from '@/lib/mood'

export type CallPhase = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error'

const PHASE_LABEL: Record<CallPhase, string> = {
  connecting: 'Connecting…',
  listening: 'Listening…',
  thinking: 'Thinking…',
  speaking: 'Speaking…',
  error: "Couldn't connect",
}

// A photo/video can't change its face on cue, so mood is carried by an ambient glow around the
// frame instead — a soft concerned ring while working through a problem, warm celebratory on a win.
const MOOD_COLOR: Record<Mood, string | null> = {
  neutral: null,
  concerned: 'var(--info-text)',
  celebratory: 'var(--warning-text)',
}

export const AvatarCallScreen = forwardRef<
  TalkingAvatarHandle,
  {
    coach: Companion
    phase: CallPhase
    mood?: Mood
    transcript: string
    muted: boolean
    onToggleMute: () => void
    onEndCall: () => void
    onStatusChange: (status: AvatarStatus) => void
  }
>(function AvatarCallScreen({ coach, phase, mood = 'neutral', transcript, muted, onToggleMute, onEndCall, onStatusChange }, ref) {
  const moodColor = MOOD_COLOR[mood]
  return (
    <div className="relative flex-1 min-h-0 bg-black">
      <TalkingAvatar ref={ref} coach={coach.id} className="absolute inset-0" onStatusChange={onStatusChange} />
      {moodColor && (
        <div
          className="avatar-mood-ring-inset pointer-events-none absolute inset-0"
          style={{ ['--mood-color' as string]: moodColor }}
        />
      )}

      {/* Top bar */}
      <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-2 bg-gradient-to-b from-black/60 to-transparent p-4">
        <div className="text-white">
          <div className="text-sm font-semibold drop-shadow">{coach.name}</div>
          <div className="text-[11px] text-white/80 drop-shadow">{coach.tagline}</div>
        </div>
        <button
          onClick={onEndCall}
          className="flex size-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25"
          aria-label="End video call"
        >
          <PhoneOff className="size-4" />
        </button>
      </div>

      {/* Bottom overlay: status + live captions + mic control */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 bg-gradient-to-t from-black/70 to-transparent px-4 pb-5 pt-10">
        <div className="flex items-center gap-2">
          {phase === 'listening' && <span className="relative flex size-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" /><span className="relative inline-flex size-2 rounded-full bg-success" /></span>}
          <span className="text-xs font-medium text-white/90 drop-shadow">{muted && phase === 'listening' ? 'Mic muted' : PHASE_LABEL[phase]}</span>
        </div>
        {transcript && (
          <p className="max-w-sm text-center text-sm text-white drop-shadow-md line-clamp-2">{transcript}</p>
        )}
        <button
          onClick={onToggleMute}
          className={cn(
            'flex size-12 items-center justify-center rounded-full backdrop-blur-sm transition-colors',
            muted ? 'bg-alert text-white' : 'bg-white/15 text-white hover:bg-white/25',
          )}
          aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
        >
          {muted ? <MicOff className="size-5" /> : <Mic className="size-5" />}
        </button>
      </div>
    </div>
  )
})
